/**
 * Game Scene Setup
 * Loads scene from server or falls back to hardcoded prototype values
 */

import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { Engine } from '../../engine/Engine';
import { sceneApi } from '../../api/sceneApi';
import { serverConfig } from '../../network/ServerConfig';
import {
  SceneLoader,
  buildSceneBodies,
  SDL_COLLISION_GROUPS,
  SDL_COLLISION_MASKS,
  type SDLCameraAnchor,
  type GameModeId,
} from '@sdl/scene-format';

export interface GameSceneConfig {
  tableWidth?: number;
  tableDepth?: number;
  wallHeight?: number;
  gameModeId?: GameModeId;
}

const DEFAULT_CONFIG = {
  tableWidth: 12,
  tableDepth: 16,
  wallHeight: 8,
};

/** A light with optional per-anchor overrides (from editor scene format) */
export interface AnchoredLight {
  light: THREE.Light;
  anchors?: Record<string, {
    color?: string;
    intensity?: number;
    position?: [number, number, number];
    target?: [number, number, number];
    distance?: number;
    decay?: number;
    angle?: number;
    penumbra?: number;
    castShadow?: boolean;
    visible?: boolean;
  }>;
}

export class GameScene {
  private engine: Engine;
  private config: GameSceneConfig & typeof DEFAULT_CONFIG;
  private shootingAnchor = new THREE.Object3D();
  private bettingAnchor = new THREE.Object3D();
  private sceneLoader: SceneLoader | null = null;
  private loadedFromServer = false;
  private anchoredLights: AnchoredLight[] = [];

  constructor(engine: Engine, config?: GameSceneConfig) {
    this.engine = engine;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Initialize the scene - tries server first, falls back to hardcoded
   */
  async initialize(): Promise<void> {
    try {
      const isBaked = await this.loadFromServer();
      this.loadedFromServer = true;
      if (isBaked) {
        console.log('🎬 Baked scene loaded from server (optimized for mobile)');
      } else {
        console.log('🎬 Scene loaded from server');
      }
    } catch (err) {
      console.warn('⚠️ Failed to load scene from server, using fallback:', err);
      this.setupFallbackScene();
    }
  }

  /**
   * Load scene from the server using SceneLoader
   * @returns true if a baked (optimized) scene was loaded
   */
  private async loadFromServer(): Promise<boolean> {
    const document = this.config.gameModeId
      ? await sceneApi.getForGameMode(this.config.gameModeId)
      : await sceneApi.getDefault();

    // Create SceneLoader with GLTFLoader for baked scene support
    this.sceneLoader = new SceneLoader({
      gltfLoader: new GLTFLoader(),
      baseUrl: serverConfig.mainServerUrl,
      preferBaked: true,  // Use baked scene if available
      maxAnisotropy: this.engine.renderer.renderer.capabilities.getMaxAnisotropy(),
    });
    const loaded = await this.sceneLoader.load(document);

    const scene = this.engine.scene;

    // Setup background from document
    if (document.settings.background.type === 'color' && document.settings.background.color) {
      scene.background = new THREE.Color(document.settings.background.color);
    }

    // Apply scene's physics gravity so client matches server. The server reads
    // document.settings.physics.gravity when constructing HiLoPhysicsWorld;
    // if we don't mirror that here, HI_LO dice fall at different rates on
    // server vs client whenever the scene's gravity differs from the default.
    if (document.settings?.physics?.gravity !== undefined) {
      this.engine.physics.setGravity(document.settings.physics.gravity);
    }

    // No fog - keep it clean like prototype
    scene.fog = null;

    // Add loaded objects to scene
    scene.add(loaded.scene as any);

    this.sceneLoader?.loadDecals(document.decals ?? [], scene as any);

    // Create physics bodies for loaded objects via shared builder.
    // The builder lives in @sdl/scene-format so the game-server's HI_LO
    // physics world uses identical geometry — critical for reconciliation.
    buildSceneBodies(this.engine.physics.world, document, {
      tableMaterial: this.engine.physics.tableMaterial,
      wallMaterial: this.engine.physics.wallMaterial,
    });

    // Setup camera anchors from document
    this.setupCameraAnchorsFromDocument(document.cameraAnchors);

    // Collect lights with anchor data for phase-based lighting
    this.collectAnchoredLights(scene);

    // Set initial camera position from first anchor
    const camera = this.engine.camera;
    const defaultAnchor = document.cameraAnchors.find(a => a.name === 'Shooting');
    if (defaultAnchor) {
      camera.position.set(...defaultAnchor.position);
      camera.lookAt(...defaultAnchor.lookAt);
    } else {
      camera.position.set(0, 14, 10);
      camera.lookAt(0, 0, 0);
    }

    // Extract table dimensions from floor object for compatibility
    const floor = document.objects.find(o => o.name === 'Floor');
    if (floor && floor.type === 'primitive' && floor.primitive.shape === 'plane') {
      this.config.tableWidth = floor.primitive.parameters.planeWidth;
      this.config.tableDepth = floor.primitive.parameters.planeHeight;
    }

    const wall = document.objects.find(o => o.name === 'Back Wall');
    if (wall && wall.type === 'primitive' && wall.primitive.shape === 'box') {
      this.config.wallHeight = wall.primitive.parameters.height;
    }

    // Raycast from betting anchor to find actual ground hit point for zone positioning
    this.raycastBettingAnchorGround(document.cameraAnchors);

    return loaded.isBaked ?? false;
  }

  /**
   * Setup camera anchors from document
   */
  private setupCameraAnchorsFromDocument(anchors: SDLCameraAnchor[]): void {
    const shootingAnchor = anchors.find(a => a.name === 'Shooting');
    const bettingAnchor = anchors.find(a => a.name === 'Betting');

    if (shootingAnchor) {
      this.shootingAnchor.name = 'Anchor.Shooting';
      this.shootingAnchor.position.set(...shootingAnchor.position);
      this.shootingAnchor.userData.lookAt = shootingAnchor.lookAt;
      this.shootingAnchor.userData.fov = shootingAnchor.fov;
    } else {
      this.shootingAnchor.name = 'Anchor.Shooting';
      this.shootingAnchor.position.set(0, 14, 10);
    }

    if (bettingAnchor) {
      this.bettingAnchor.name = 'Anchor.Betting';
      this.bettingAnchor.position.set(...bettingAnchor.position);
      this.bettingAnchor.userData.lookAt = bettingAnchor.lookAt;
      this.bettingAnchor.userData.fov = bettingAnchor.fov;
    } else {
      this.bettingAnchor.name = 'Anchor.Betting';
      this.bettingAnchor.position.set(0, 0.01, -0.8);
    }

    this.engine.scene.add(this.shootingAnchor);
    this.engine.scene.add(this.bettingAnchor);
  }

  /**
   * Optimize shadows for mobile devices
  /**
   * Fallback: Setup scene exactly like prototype (hardcoded)
   */
  private setupFallbackScene(): void {
    this.setupScene();
    this.setupLighting();
    this.setupFloor();
    this.setupWall();
    this.setupAnchors();
  }

  /**
   * Setup scene exactly like prototype
   */
  private setupScene(): void {
    const scene = this.engine.scene;

    // Background color - exactly like prototype
    scene.background = new THREE.Color(0x1a1a1a);

    // No fog - prototype doesn't use fog
    scene.fog = null;

    // Camera position - exactly like prototype
    const camera = this.engine.camera;
    camera.position.set(0, 14, 10);
    camera.lookAt(0, 0, 0);
  }

  /**
   * Setup lighting exactly like prototype
   */
  private setupLighting(): void {
    const scene = this.engine.scene;

    // Clear any existing lights
    scene.children
      .filter((c) => c instanceof THREE.Light)
      .forEach((l) => scene.remove(l));

    // Ambient light - prototype: 0xffffff, 0.4
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
    scene.add(ambientLight);

    // Main directional light - prototype values
    const mainLight = new THREE.DirectionalLight(0xffffff, 1.2);
    mainLight.position.set(5, 15, 5);
    mainLight.castShadow = true;
    // 2048 to match the 'high' quality tier (re-applied after scene load via
    // engine.setQuality). 1024 looked blocky at the close shooting camera.
    mainLight.shadow.mapSize.width = 2048;
    mainLight.shadow.mapSize.height = 2048;
    mainLight.shadow.camera.near = 1;
    mainLight.shadow.camera.far = 30;
    mainLight.shadow.camera.left = -15;
    mainLight.shadow.camera.right = 15;
    mainLight.shadow.camera.top = 15;
    mainLight.shadow.camera.bottom = -15;
    // No shadow bias: it eats the contact shadow when a die rests on the floor.
    // The pip shadow-acne that motivated a bias is solved at the source by the
    // dice being receiveShadow=false, and the flat floor (lit near head-on) has
    // no acne of its own — so default bias (0) gives clean contact shadows.
    scene.add(mainLight);

    // Rim light - prototype: 0xffd700, 0.3
    const rimLight = new THREE.DirectionalLight(0xffd700, 0.3);
    rimLight.position.set(-5, 10, -5);
    scene.add(rimLight);
  }

  /**
   * Setup floor exactly like prototype
   */
  private setupFloor(): void {
    const { tableWidth, tableDepth } = this.config;
    const scene = this.engine.scene;
    const physics = this.engine.physics;

    // Floor geometry - exactly like prototype
    const floorGeometry = new THREE.PlaneGeometry(tableWidth, tableDepth);

    // Floor material - prototype: 0x1a472a, roughness 0.9, metalness 0
    const floorMaterial = new THREE.MeshStandardMaterial({
      color: 0x1a472a,
      roughness: 0.9,
      metalness: 0.0,
    });

    const floor = new THREE.Mesh(floorGeometry, floorMaterial);
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    scene.add(floor);

    // Floor physics - static plane exactly like prototype
    const floorShape = new CANNON.Plane();
    const floorBody = new CANNON.Body({
      type: CANNON.Body.STATIC,
      shape: floorShape,
      material: physics.tableMaterial,
      collisionFilterGroup: SDL_COLLISION_GROUPS.TABLE,
      collisionFilterMask: SDL_COLLISION_MASKS.TABLE,
    });
    floorBody.quaternion.setFromEuler(-Math.PI / 2, 0, 0);
    physics.world.addBody(floorBody);

    console.log(`🏓 Floor physics body created with collision group ${floorBody.collisionFilterGroup}`);
  }

  /**
   * Setup wall exactly like prototype - ONLY back wall
   */
  private setupWall(): void {
    const { tableWidth, tableDepth, wallHeight } = this.config;
    const scene = this.engine.scene;
    const physics = this.engine.physics;

    const wallThickness = 0.5;
    const wallColor = 0x3d2817;

    const wallWidth = tableWidth + wallThickness * 2;

    const wallGeometry = new THREE.BoxGeometry(wallWidth, wallHeight, wallThickness);
    const wallMaterial = new THREE.MeshStandardMaterial({
      color: wallColor,
      roughness: 0.7,
      metalness: 0.2,
    });

    const wall = new THREE.Mesh(wallGeometry, wallMaterial);
    wall.position.set(0, wallHeight / 2, -tableDepth / 2 - wallThickness / 2);
    wall.castShadow = true;
    wall.receiveShadow = true;
    scene.add(wall);

    // Wall physics
    const wallShape = new CANNON.Box(
      new CANNON.Vec3(wallWidth / 2, wallHeight / 2, wallThickness / 2)
    );
    const wallBody = new CANNON.Body({
      type: CANNON.Body.STATIC,
      shape: wallShape,
      material: physics.wallMaterial,
      collisionFilterGroup: SDL_COLLISION_GROUPS.WALL,
      collisionFilterMask: SDL_COLLISION_MASKS.WALL,
    });
    wallBody.position.set(0, wallHeight / 2, -tableDepth / 2 - wallThickness / 2);
    physics.world.addBody(wallBody);
  }

  private setupAnchors(): void {
    this.shootingAnchor.name = 'Anchor.Shooting';
    this.shootingAnchor.position.set(0, 0.01, 0);

    this.bettingAnchor.name = 'Anchor.Betting';
    this.bettingAnchor.position.set(0, 0.01, -0.8);

    this.engine.scene.add(this.shootingAnchor);
    this.engine.scene.add(this.bettingAnchor);
  }

  /**
   * Walk the scene and collect all lights that have sdlAnchors userData
   * (set by SceneLoader from the document's anchors field).
   */
  private collectAnchoredLights(scene: THREE.Scene): void {
    this.anchoredLights = [];
    scene.traverse((obj) => {
      if (obj instanceof THREE.Light) {
        const anchors = obj.userData.sdlAnchors;
        this.anchoredLights.push({ light: obj, anchors: anchors ?? undefined });
      }
    });
    if (this.anchoredLights.length > 0) {
      console.log(`💡 Collected ${this.anchoredLights.length} scene lights for phase-based lighting`);
    }
  }

  /**
   * Get all lights with anchor data for phase-based control
   */
  getAnchoredLights(): AnchoredLight[] {
    return this.anchoredLights;
  }

  getCameraAnchors(): { shooting: THREE.Object3D; betting: THREE.Object3D } {
    return { shooting: this.shootingAnchor, betting: this.bettingAnchor };
  }

  /**
   * Get table bounds for camera calculations
   */
  getTableBounds(): { width: number; depth: number } {
    return {
      width: this.config.tableWidth,
      depth: this.config.tableDepth,
    };
  }

  /**
   * Get betting element positions
   */
  getBettingPositions(): {
    wallet: { x: number; y: number; z: number };
    bettingZone: { x: number; y: number; z: number };
  } {
    return {
      wallet: { x: 2, y: 1.8, z: 3 },
      bettingZone: { x: 0, y: 0.01, z: 0 },
    };
  }

  /**
   * Whether the scene was loaded from server or using fallback
   */
  isLoadedFromServer(): boolean {
    return this.loadedFromServer;
  }

  /**
   * Update scene (for animated elements)
   */
  update(_delta: number): void {
    // Nothing to update - simple static scene like prototype
  }

  /**
   * Debug: raycast from betting anchor camera position toward lookAt
   * to see what scene objects are in the line of sight
   */
  /**
   * Raycast from the betting anchor camera position through its lookAt direction
   * to find the actual ground hit point. Stores result on bettingAnchor.userData.groundHit
   * so GameFlowController can use it for zone positioning.
   */
  private raycastBettingAnchorGround(anchors: SDLCameraAnchor[]): void {
    const bettingAnchor = anchors.find(a => a.name === 'Betting');
    if (!bettingAnchor) return;

    // Force world matrix update — meshes just added may not have current transforms
    this.engine.scene.updateMatrixWorld(true);

    const origin = new THREE.Vector3(...bettingAnchor.position);
    const lookAt = new THREE.Vector3(...bettingAnchor.lookAt);
    const direction = new THREE.Vector3().subVectors(lookAt, origin).normalize();

    const allMeshes: THREE.Mesh[] = [];
    this.engine.scene.traverse((obj) => {
      if (obj instanceof THREE.Mesh) allMeshes.push(obj);
    });

    const raycaster = new THREE.Raycaster(origin, direction);
    const hits = raycaster.intersectObjects(allMeshes, false);

    if (hits.length > 0) {
      const hit = hits[0];
      this.bettingAnchor.userData.groundHit = [hit.point.x, hit.point.y, hit.point.z];
      console.log(`🎯 Betting anchor ground hit: [${hit.point.x.toFixed(2)}, ${hit.point.y.toFixed(2)}, ${hit.point.z.toFixed(2)}] on "${hit.object.name || hit.object.parent?.name || '(unnamed)'}"`);
    } else {
      console.warn('🎯 Betting anchor raycast missed all geometry — falling back to lookAt XZ');
    }
  }

  /**
   * Cleanup resources
   */
  dispose(): void {
    if (this.sceneLoader) {
      this.sceneLoader.dispose();
      this.sceneLoader = null;
    }
  }
}
